import type { SubscriptionView } from '@/types/api';

/**
 * Free-trial helpers, recomputed against the current clock.
 *
 * The server stamps `subscription.isInTrial` at fetch time, but the whole
 * AccountView is persisted to localStorage and `ProtectedRoute` skips the
 * bootstrap refetch on returning visits — so a frozen `isInTrial=true` would
 * keep granting access after the trial actually expired. Gate on these
 * functions (which compare `trialEndsAt` to `Date.now()`) instead of trusting
 * the snapshotted boolean.
 */

/** Whole days remaining until the trial ends (rounded up); 0 when none/expired. */
export function trialDaysRemaining(trialEndsAt: string | null): number {
  if (!trialEndsAt) return 0;
  const end = Date.parse(trialEndsAt);
  if (Number.isNaN(end)) return 0;
  const ms = end - Date.now();
  if (ms <= 0) return 0;
  return Math.ceil(ms / (24 * 60 * 60 * 1000));
}

/**
 * True when the account is inside a still-valid free trial. Mirrors the
 * server's `computeEntitlement`: a real paying subscriber is never "in trial",
 * and the window is judged live against the current clock (not the persisted
 * boolean). When this is true, `trialDaysRemaining` is always >= 1.
 */
export function isTrialActive(subscription: SubscriptionView | null | undefined): boolean {
  if (!subscription) return false;
  if (subscription.isPaid) return false;
  return trialDaysRemaining(subscription.trialEndsAt) > 0;
}
